import styled, { css } from "styled-components";
import { Tabs } from "antd";
import {
  brand,
  border,
  status,
  surface,
  text,
  typography,
  radius,
} from "../../theme/tokens";

// ---------------------------------------------------------------------------
// Page shell (visual language matches the Mail Intake page)
// ---------------------------------------------------------------------------

export const FaxPageWrapper = styled.div`
  font-family: ${typography.fontFamily};
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${surface.surface};
`;

export const FaxNavTabs = styled(Tabs)`
  &.ant-tabs {
    padding: 0 24px;
    background: ${surface.surface};
    border-bottom: 1px solid ${border.default};

    .ant-tabs-nav { margin: 0; }
    .ant-tabs-nav-list { gap: 0; }

    .ant-tabs-tab {
      padding: 14px 20px;
      margin: 0;
      .ant-tabs-tab-btn {
        font-family: ${typography.fontFamily};
        font-weight: ${typography.weight.medium};
        font-size: 14px;
        color: ${text.tertiary};
      }
    }
    .ant-tabs-tab:focus,
    .ant-tabs-tab:focus-visible { outline: none !important; box-shadow: none !important; }
    .ant-tabs-tab-active .ant-tabs-tab-btn { font-weight: ${typography.weight.semibold}; color: ${brand.primary} !important; }
    .ant-tabs-ink-bar { background: ${brand.primary}; height: 3px; }
  }
`;

export const ViewContainer = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Send tab
// ---------------------------------------------------------------------------

export const SendLayout = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow-y: auto;
  gap: 28px;
`;

export const SendFormCard = styled.div`
  max-width: 560px;
  padding: 24px;
  background: #f9fafb;
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const SendFormTitle = styled.div`
  font-size: 16px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${text.tertiary};
`;

export const TextInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  color: ${text.primary};
  outline: none;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s;

  &::placeholder { color: ${text.disabled}; }
  &:focus { border-color: ${brand.primary}; box-shadow: 0 0 0 2px rgba(12, 83, 187, 0.08); }
`;

export const FileDropZone = styled.div`
  padding: 28px 20px;
  border: 2px dashed ${({ active }) => (active ? brand.primary : border.default)};
  border-radius: ${radius.lg};
  background: ${({ active }) => (active ? brand.primaryLight : surface.surface)};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${text.disabled};
  font-size: 13px;

  &:hover { border-color: ${brand.primary}; background: #f8faff; }
`;

export const SelectedFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f0f7ff;
  border: 1px solid #c3d5f1;
  border-radius: ${radius.md};
  font-size: 13px;
  color: ${text.primary};
`;

export const ClearFileBtn = styled.button`
  background: none;
  border: none;
  color: ${text.tertiary};
  cursor: pointer;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  border-radius: ${radius.sm};
  &:hover { color: ${status.failed.fg}; background: ${status.failed.bg}; }
`;

export const PrimaryBtn = styled.button`
  padding: 11px 20px;
  background: ${brand.primary};
  color: ${text.onAccent};
  border: none;
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) { background: ${brand.primaryHover}; }
  &:disabled { background: #c3d5f1; cursor: not-allowed; }
`;

export const SendHint = styled.div`
  font-size: 12px;
  color: ${text.disabled};
`;

export const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ---------------------------------------------------------------------------
// Log tab
// ---------------------------------------------------------------------------

export const LogViewWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  overflow: hidden;
`;

export const LogToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const LogSearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 360px;
  padding: 8px 14px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  color: ${text.primary};
  outline: none;

  &::placeholder { color: ${text.disabled}; }
  &:focus { border-color: ${brand.primary}; }
`;

export const LogTableWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

// Tone map spans both directions: received-fax dispositions and
// outbound statuses render through the same pill.
const FAX_STATUS_TONES = {
  // received dispositions
  in_sorting_queue: status.review,
  assigned: status.ready,
  deleted: status.failed,
  // outbound statuses
  queued: status.processing,
  sending: status.processing,
  sent: status.ready,
  failed: status.failed,
};

export const FaxStatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: ${radius.xl};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  ${({ tone }) => {
    const resolved = FAX_STATUS_TONES[tone] || status.neutral;
    return css`
      background: ${resolved.bg};
      color: ${resolved.fg};
      border: 1px solid ${resolved.border};
    `;
  }}
`;

export const MetaText = styled.span`
  color: ${text.tertiary};
  font-size: 13px;
`;

export const PreviewFrame = styled.iframe`
  width: 100%;
  height: 72vh;
  border: none;
  background: #525659;
`;
